import type { ChoiceGroup, FoodOption, MealMenu, Regimen } from "@/data/meal-plan/types.ts";
import { VEGETABLES_FRESH } from "@/data/meal-plan/options.ts";
import {
  ROMINA2_AVOCADO_40,
  ROMINA2_AVOCADO_80,
  ROMINA2_BANANA,
  ROMINA2_BLUEBERRIES,
  ROMINA2_CHIA,
  ROMINA2_DINNER_LEGUMES,
  ROMINA2_DINNER_POTATO,
  ROMINA2_DINNER_RICE_OR_PASTA,
  ROMINA2_EGG,
  ROMINA2_FILLETS_WITH_PORK,
  ROMINA2_FILLETS_WITH_TURKEY,
  ROMINA2_FRUIT_PORTION,
  ROMINA2_GROUND_BEEF,
  ROMINA2_LIGHT_CHEESE,
  ROMINA2_LUNCH_LEGUMES,
  ROMINA2_LUNCH_POTATO,
  ROMINA2_LUNCH_RICE_OR_PASTA,
  ROMINA2_OATS,
  ROMINA2_OLIVE_OIL,
  ROMINA2_PINEAPPLE,
  ROMINA2_POZIMA,
  ROMINA2_PRO_DAY,
  ROMINA2_QUARK_OR_SKYR,
  ROMINA2_SALMAS,
  ROMINA2_TUNA_CAN,
  ROMINA2_VAKIMU_PRO_DRINK,
  ROMINA2_WATERMELON,
  ROMINA2_WHEY_HALF_SCOOP,
  ROMINA2_WHEY_SCOOP,
} from "./options.ts";

export type { FoodOption, ChoiceGroup, Meal, Regimen } from "@/data/meal-plan/types.ts";

type Category = ChoiceGroup["category"];

/** A fixed food inside a menu: one option, picked `count` times. */
function fixed(category: Category, option: FoodOption, count = 1): ChoiceGroup {
  return { category, pick: count, canRepeat: count > 1, options: [option] };
}

/** An inner alternative ("arroz o fideos"): pick one of several options. */
function oneOf(category: Category, options: FoodOption[]): ChoiceGroup {
  return { category, pick: 1, canRepeat: false, options };
}

/** "1 bowl de ensalada fresca o al vapor". */
const salad = (): ChoiceGroup => oneOf("vegetable", VEGETABLES_FRESH);

function menus(...items: { summary: string; choices: ChoiceGroup[] }[]): MealMenu[] {
  return items.map((item, i) => ({ id: String(i + 1), name: `Opción ${i + 1}`, ...item }));
}

export const romina2Regimen: Regimen = {
  id: "romina-nutri-2",
  name: "Plan diario",
  badge: "N",
  description:
    "Recomposición corporal (objetivo de la nutricionista: 1261 kcal). Elige 1 de 4 opciones por comida.",
  notes: [
    "Creatina: 6 g todos los días, ni bien te levantes",
    "Citrato de magnesio: 400 a 600 mg, 1 hora antes de dormir",
  ],
  meals: [
    {
      name: "breakfast",
      label: "Desayuno",
      time: "9:00 am",
      choices: [],
      menus: menus(
        {
          summary:
            "Pancakes (20 g avena + 2 huevos + 80 g plátano + 1 cda chía) + 100 g yogurt Quark o Skyr + café o infusión sin azúcar",
          choices: [
            fixed("carb", ROMINA2_OATS),
            fixed("protein", ROMINA2_EGG, 2),
            fixed("fruit", ROMINA2_BANANA),
            fixed("fat", ROMINA2_CHIA),
            oneOf("protein", ROMINA2_QUARK_OR_SKYR),
          ],
        },
        {
          summary:
            "180 g piña + 1 paquete Salmas + 2 huevos + 80 g queso light + 40 g palta + café o infusión sin azúcar",
          choices: [
            fixed("fruit", ROMINA2_PINEAPPLE),
            fixed("carb", ROMINA2_SALMAS),
            fixed("protein", ROMINA2_EGG, 2),
            fixed("protein", ROMINA2_LIGHT_CHEESE),
            fixed("fat", ROMINA2_AVOCADO_40),
          ],
        },
        {
          summary:
            "270 g sandía + 105 g arándanos + 80 g queso light + 2 huevos + 40 g palta + café o infusión sin azúcar",
          choices: [
            fixed("fruit", ROMINA2_WATERMELON),
            fixed("fruit", ROMINA2_BLUEBERRIES),
            fixed("protein", ROMINA2_LIGHT_CHEESE),
            fixed("protein", ROMINA2_EGG, 2),
            fixed("fat", ROMINA2_AVOCADO_40),
          ],
        },
        {
          summary:
            "Pancakes (20 g avena + ½ scoop de proteína + 2 huevos + 80 g plátano + 1 cda chía) + café o infusión sin azúcar",
          choices: [
            fixed("carb", ROMINA2_OATS),
            fixed("protein", ROMINA2_WHEY_HALF_SCOOP),
            fixed("protein", ROMINA2_EGG, 2),
            fixed("fruit", ROMINA2_BANANA),
            fixed("fat", ROMINA2_CHIA),
          ],
        },
      ),
    },
    {
      name: "lunch",
      label: "Almuerzo",
      time: "1:00 pm",
      note: "Evitar frituras o empanizados",
      choices: [],
      menus: menus(
        {
          summary:
            "100 g arroz o fideos + 150 g filete de pollo, pescado, cerdo o res + 1 bowl de ensalada fresca o al vapor + 2 cdtas aceite de oliva (10 g)",
          choices: [
            oneOf("carb", ROMINA2_LUNCH_RICE_OR_PASTA),
            oneOf("protein", ROMINA2_FILLETS_WITH_PORK),
            salad(),
            fixed("fat", ROMINA2_OLIVE_OIL, 2),
          ],
        },
        {
          summary:
            "160 g papa + 1 lata de atún + 2 huevos + 1 bowl de ensalada fresca o al vapor + 80 g palta",
          choices: [
            fixed("carb", ROMINA2_LUNCH_POTATO),
            fixed("protein", ROMINA2_TUNA_CAN),
            fixed("protein", ROMINA2_EGG, 2),
            salad(),
            fixed("fat", ROMINA2_AVOCADO_80),
          ],
        },
        {
          summary:
            "150 g menestras + 150 g filete de pollo, pavita, pescado o res + 1 bowl de ensalada fresca o al vapor + 2 cdtas aceite de oliva (10 g)",
          choices: [
            oneOf("carb", ROMINA2_LUNCH_LEGUMES),
            oneOf("protein", ROMINA2_FILLETS_WITH_TURKEY),
            salad(),
            fixed("fat", ROMINA2_OLIVE_OIL, 2),
          ],
        },
        {
          summary:
            "160 g papa + 150 g carne molida premium + 1 bowl de ensalada fresca o al vapor + 80 g palta",
          choices: [
            fixed("carb", ROMINA2_LUNCH_POTATO),
            fixed("protein", ROMINA2_GROUND_BEEF),
            salad(),
            fixed("fat", ROMINA2_AVOCADO_80),
          ],
        },
      ),
    },
    {
      name: "mid-afternoon",
      label: "Media Tarde",
      time: "5:00 pm",
      choices: [],
      menus: menus(
        {
          summary: "1 botella Pozima",
          choices: [fixed("protein", ROMINA2_POZIMA)],
        },
        {
          summary: "1 porción de fruta + 1 scoop de proteína isolate",
          choices: [
            oneOf("fruit", ROMINA2_FRUIT_PORTION),
            fixed("protein", ROMINA2_WHEY_SCOOP),
          ],
        },
        {
          summary: "1 botella de Vakimu +Pro (350 g)",
          choices: [fixed("protein", ROMINA2_VAKIMU_PRO_DRINK)],
        },
        {
          summary: "1 botella Pro Day Gloria",
          choices: [fixed("protein", ROMINA2_PRO_DAY)],
        },
      ),
    },
    {
      name: "dinner",
      label: "Cena",
      time: "8:00 pm",
      choices: [],
      menus: menus(
        {
          summary:
            "50 g arroz o fideos + 150 g filete de pollo, pescado, cerdo o res + 1 bowl de ensalada fresca o al vapor + 1 cdta aceite de oliva (5 g)",
          choices: [
            oneOf("carb", ROMINA2_DINNER_RICE_OR_PASTA),
            oneOf("protein", ROMINA2_FILLETS_WITH_PORK),
            salad(),
            fixed("fat", ROMINA2_OLIVE_OIL),
          ],
        },
        {
          summary:
            "80 g papa + 1 lata de atún + 2 huevos + 1 bowl de ensalada fresca o al vapor + 40 g palta",
          choices: [
            fixed("carb", ROMINA2_DINNER_POTATO),
            fixed("protein", ROMINA2_TUNA_CAN),
            fixed("protein", ROMINA2_EGG, 2),
            salad(),
            fixed("fat", ROMINA2_AVOCADO_40),
          ],
        },
        {
          summary:
            "75 g menestras + 150 g filete de pollo, pavita, pescado o res + 1 bowl de ensalada fresca o al vapor + 1 cdta aceite de oliva (5 g)",
          choices: [
            oneOf("carb", ROMINA2_DINNER_LEGUMES),
            oneOf("protein", ROMINA2_FILLETS_WITH_TURKEY),
            salad(),
            fixed("fat", ROMINA2_OLIVE_OIL),
          ],
        },
        {
          summary:
            "80 g papa + 150 g carne molida premium + 1 bowl de ensalada fresca o al vapor + 40 g palta",
          choices: [
            fixed("carb", ROMINA2_DINNER_POTATO),
            fixed("protein", ROMINA2_GROUND_BEEF),
            salad(),
            fixed("fat", ROMINA2_AVOCADO_40),
          ],
        },
      ),
    },
  ],
};

export const romina2Regimens: Regimen[] = [romina2Regimen];
